/**
 * §9.9.2 — the achievement catalogue, and the gates it opens.
 *
 * ## Why this file exists at all
 *
 * `meta.achievements` and `meta.unlocks` have shipped in `src/core/save.ts` since the
 * first build. Both are read. Neither has ever been *written*: no table named the
 * achievements, so no code could award one, so `unlocks.weapons` and its five siblings
 * have been empty arrays on every save file in existence. The effect is that 8 of 12
 * weapons, 14 of 25 passives, 2 of 3 arenas, 2 of 3 difficulty tiers and 1 of 2 actives
 * are gated behind a door with no key. See AMD-49.
 *
 * This table is the key's blank. It names each achievement, states its condition in the
 * words the player reads, and lists what the condition opens.
 *
 * ## Why the copy and the rule live together
 *
 * `condition` is a descriptor, not prose, and `label` is the same rule in English. They
 * sit in one record on purpose: the Archive prints `label` and the evaluator switches on
 * `condition`, so a gate whose text drifts from its rule is a diff you can see rather
 * than a bug a player finds. `src/ui/screens/loadout.ts` already makes this argument for
 * the arena table (§10.8 has no `arenas.json` either).
 *
 * The long-term home for the condition half is `src/content/` beside every other tuning
 * table, with the evaluator in `src/sim/progression.ts`. It is here because the UI is
 * what exists today; moving it is a rename, not a redesign.
 *
 * ## What this file is NOT
 *
 * It is not a writer. `src/game.ts` owns `this.meta` and is the only thing that may call
 * `saveMeta`; a second writer against `lumenabyss.meta.v1` would reproduce the collision
 * that `core/save.ts` and `ui/settings-store.ts` once had over one key, where each side
 * read the other's envelope as corrupt and deleted it every boot. The Archive screen
 * reads. It never writes.
 *
 * ## Unlock ≠ power (§9.9)
 *
 * Every gate below opens a *starting condition*: an arena to descend into, a difficulty
 * to pick, a weapon to open the run holding. None of them raises a number on a build the
 * player already has. A meta layer that sold stats would make the tenth run stronger than
 * the first, which is the one thing a run-based game cannot trade away.
 */

/**
 * A condition the evaluator can settle from state that already exists: the `MetaV1`
 * stats block, plus the `RunSummary` the integrator builds at the end of every run.
 *
 * `lifetime_dashes` is the exception and is deliberately left in. `ach.maw-instinct`
 * with its 250-dash bar is printed on the Loadout screen today, so dropping it here
 * would leave shipped copy promising a gate the catalogue does not carry. `MetaV1.stats`
 * has no dash counter, so this one cannot fire until `stats` grows a `dashes` field —
 * a save-schema change with a migration, and `core/save.ts` owns it.
 */
export type AchievementCondition =
  | { kind: 'victory' }
  | { kind: 'victory_on'; difficulty_id: string }
  | { kind: 'lifetime_dashes'; count: number };

/** The five lists in `MetaV1.unlocks`. Empty keys are omitted rather than left `[]`. */
export interface UnlockGrant {
  weapons?: readonly string[];
  passives?: readonly string[];
  actives?: readonly string[];
  arenas?: readonly string[];
  diff?: readonly string[];
}

export interface Achievement {
  id: string;
  /** Display name. Pixel face, so it stays short enough to survive uppercasing. */
  title: string;
  /** The condition in the player's words. Must say the same thing as `condition`. */
  label: string;
  condition: AchievementCondition;
  grants: UnlockGrant;
  /**
   * True when no code path can currently satisfy the condition, for reasons the player
   * did nothing to cause. The Archive says so out loud instead of showing a bar that
   * cannot move.
   */
  blocked?: boolean;
}

/**
 * The gates that shipped copy already promises, and nothing else.
 *
 * Three of these are printed verbatim on the Loadout screen right now
 * (`ARENAS[].unlock_text`, `ACTIVES[].unlock_text`); two more are the difficulty
 * ladder's own `unlock` strings out of `difficulty-modifiers.json`. Every one of them
 * was specified by someone else and is transcribed, not invented.
 *
 * The 8 locked weapons and 14 locked passives have no gate here because none has ever
 * been specified. Picking which run earns the Brine Pool is a content decision with a
 * balance argument attached, and it belongs to whoever owns `src/content/`. Inventing
 * 22 of them from the UI layer would be guessing loudly.
 */
export const ACHIEVEMENTS: readonly Achievement[] = [
  {
    id: 'ach.deep-victory',
    title: 'Deep Victory',
    label: 'Win a run. Kill the Hadal Sovereign.',
    condition: { kind: 'victory' },
    grants: { arenas: ['arena.smoker-gardens'] },
  },
  {
    id: 'ach.descent-1-clear',
    title: 'Descent I Cleared',
    label: 'Win a run on Descent I.',
    condition: { kind: 'victory_on', difficulty_id: 'diff.descent-1' },
    grants: { arenas: ['arena.hadal-gate'], diff: ['diff.descent-2'] },
  },
  {
    id: 'ach.descent-2-clear',
    title: 'Descent II Cleared',
    label: 'Win a run on Descent II.',
    condition: { kind: 'victory_on', difficulty_id: 'diff.descent-2' },
    grants: { diff: ['diff.descent-3'] },
  },
  {
    id: 'ach.maw-instinct',
    title: 'Maw Instinct',
    label: 'Dash 250 times.',
    condition: { kind: 'lifetime_dashes', count: 250 },
    grants: { actives: ['weapon.gulper-maw'] },
    blocked: true,
  },
];

export function achievementById(id: string): Achievement | undefined {
  for (let i = 0; i < ACHIEVEMENTS.length; i++) {
    if (ACHIEVEMENTS[i].id === id) return ACHIEVEMENTS[i];
  }
  return undefined;
}

/** Flattens a grant into `"3 unlocks"`-style copy. Order matches the Archive's ledger. */
export function grantIds(g: UnlockGrant): readonly string[] {
  const out: string[] = [];
  const lists = [g.weapons, g.passives, g.actives, g.arenas, g.diff];
  for (let i = 0; i < lists.length; i++) {
    const l = lists[i];
    if (l === undefined) continue;
    for (let j = 0; j < l.length; j++) out.push(l[j]);
  }
  return out;
}
